import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { runtimeEnvironment } from '../../../environments/environment';
import { ISimpleTableAction, ISimpleTableColumn, ISimpleTableDropDown, ISimpleTableRow } from './simple-table.interfaces';

enum SORT_DIRN {
  UNSORTED,
  ASC,
  DESC,
}
type ColumnState = { column: ISimpleTableColumn; sortDirn: SORT_DIRN };
type RowState = { row: ISimpleTableRow; index: number; visible: boolean };

@Component({
  selector: 'app-kp-simple-table',
  templateUrl: './simple-table.component.html',
  styleUrls: ['./simple-table.scss', './chrome-extension.simple-table.scss', './web-app.simple-table.scss'],
  standalone: false,
})
export class SimpleTableComponent implements OnInit {
  renderMode = runtimeEnvironment.renderMode;

  _columnHeadings: ColumnState[] = [];
  @Input()
  set columnHeadings(headings: ISimpleTableColumn[]) {
    this._columnHeadings = headings.map((h) => {
      return { column: h, sortDirn: SORT_DIRN.UNSORTED };
    });
  }

  lastColumnSorted: ColumnState;
  _items: RowState[] = [];
  @Input()
  set items(rows: ISimpleTableRow[]) {
    this._items = [];
    rows.forEach((r, i) => {
      this._items.push({ row: r, index: i, visible: true });

      if (this.lastColumnSorted) {
        this.sortColumn(this.lastColumnSorted);
      }
    });
  }

  @Input()
  isLoading = false;

  @Input()
  canReorder = false;

  @Input()
  selectableRows = false;

  @Input()
  tableClasses = '';

  @Input()
  skeletonRowInstances = 5;

  @Input()
  skeletonRowClasses = '';

  @Input()
  searchFn: (searchText: string, rowData: any) => boolean;

  // @Input()
  // actions: ISimpleTableAction[] = [];

  // @Input()
  // data: ISimpleTableData | undefined;

  @Output()
  rowClick = new EventEmitter<any>();

  @Output()
  rowDoubleClick = new EventEmitter<any>();

  @Output()
  actionClick = new EventEmitter<{ item: any; action: any }>();

  @Output()
  itemsReordered = new EventEmitter<ISimpleTableRow[]>();

  @ViewChild('dropdowncontainer')
  dropdownContainer: ElementRef | undefined;

  hide = true;

  private valueChangesSubject = new Subject<string>();
  private valueChanges$ = this.valueChangesSubject.asObservable();

  constructor(private renderer: Renderer2) {
    this.valueChanges$
      .pipe(debounceTime(400))
      .subscribe((searchText) => this._items.forEach((rowState) => (rowState.visible = this.searchFn(searchText, rowState.row.data))));
  }

  ngOnInit(): void {
    if (this.selectableRows) {
      this.tableClasses = this.tableClasses + ' selectable-rows';
    }
  }

  @HostListener('click', ['$event'])
  @HostListener('document:click', ['$event'])
  // @HostListener('wheel', ['$event'])
  // @HostListener('scroll', ['$event'])
  @HostListener('document:scroll', ['$event'])
  @HostListener('window:scroll', ['$event'])
  hideDropDown($event) {
    this.hide = true;
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      this.hide = true;
    }
  }

  columnClick(columnState: ColumnState): void {
    const column = columnState.column;
    if (!column.sortFunction) {
      return;
    }

    this.lastColumnSorted = columnState;

    columnState.sortDirn = (columnState.sortDirn + 1) % 3;
    this.sortColumn(columnState);
  }

  itemClick(item: ISimpleTableRow): void {
    // console.log('itemClick', item);
    if (this.selectableRows) {
      const checkboxCellIndex = item.cells.findIndex((c) => c.checkbox);

      const itemInitialState = item.selected;
      this._items.forEach((r) => {
        r.row.selected = false;
        if (checkboxCellIndex > -1) {
          r.row.cells[checkboxCellIndex].checkbox.checked = false;
        }
      });
      item.selected = !itemInitialState;

      if (checkboxCellIndex > -1) {
        item.cells[checkboxCellIndex].checkbox.checked = item.selected;
      }
    }
    this.hide = true;
    this.rowClick.emit(item.selected ? item.data : null);
  }

  isRowFocusable(row: ISimpleTableRow): boolean {
    return this.selectableRows && !row.disabled;
  }

  rowKeyActivate($event: Event, row: ISimpleTableRow): void {
    if (!this.isRowFocusable(row)) {
      return;
    }

    $event.preventDefault();
    this.itemClick(row);
  }

  itemDoubleClick($event, item: ISimpleTableRow): void {
    // console.log('itemDoubleClick', item);
    this.hide = true;
    this.rowDoubleClick.emit(item.data);
    $event.stopPropagation();
  }

  itemActionClick($event, item: ISimpleTableRow, action: ISimpleTableAction): void {
    this.hide = true;
    this.actionClick.emit({ item: item.data, action: action.meta });
    $event.stopPropagation();
  }

  clearSelection() {
    this._items.forEach((value) => (value.row.selected = false));
  }

  reorder(previousIndex: number, currentIndex: number) {
    const tmpRow = this._items[currentIndex].row;
    this._items[currentIndex].row = this._items[previousIndex].row;
    this._items[previousIndex].row = tmpRow;
    this.itemsReordered.emit(this._items.map((rs) => rs.row));
  }

  drop(event: { previousIndex: number; currentIndex: number }) {
    this.reorder(event.previousIndex, event.currentIndex);
  }

  isDropDown(action: ISimpleTableAction | ISimpleTableDropDown): boolean {
    return 'actions' in action;
  }

  dropClick(e: any, dropDownAction: any, item: ISimpleTableRow): void {
    this.dropdownContainer.nativeElement.style.top = e.clientY - 0 + 'px';
    this.dropdownContainer.nativeElement.style.left = e.clientX - 0 + 'px';

    // delete root parent - not the container
    let root = this.dropdownContainer.nativeElement.firstElementChild;
    this.renderer.removeChild(this.dropdownContainer.nativeElement, root);
    root = this.renderer.createElement('span');

    dropDownAction.actions.forEach((action) => {
      const div = this.renderer.createElement('div');
      this.renderer.addClass(div, 'dd-item');
      this.renderer.listen(div, 'click', () => {
        this.itemActionClick(null, item, action);
        return false;
      });
      const text = this.renderer.createText(action.title);
      this.renderer.appendChild(div, text);
      this.renderer.appendChild(root, div);
    });

    this.renderer.appendChild(this.dropdownContainer.nativeElement, root);

    this.hide = false;
  }

  search(searchText: string): void {
    this.valueChangesSubject.next(searchText);
  }

  rowClassBuilder(itemRow): string {
    const selectedClass = itemRow.selected ? 'selected' : '';
    const disabledClass = itemRow.disabled ? 'disabled' : '';
    return selectedClass + ' ' + disabledClass;
  }

  private sortColumn(columnState: ColumnState): void {
    this._items.sort((a, b) => {
      if (columnState.sortDirn === SORT_DIRN.UNSORTED) {
        return a.index - b.index;
      }

      const result = columnState.column.sortFunction(a.row.data, b.row.data);
      return columnState.sortDirn === SORT_DIRN.ASC ? result : -1 * result;
    });
  }
}
